<template>
  <div class="col-sm-12 col-xl-6">
    <div class="card">
      <div class="card-header pb-0 d-flex justify-content-between align-items-center">
        <div>
          <h5 class="mb-1">Estado del Backend</h5>
          <span class="text-muted small">Validación en tiempo real de `GET /health`</span>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          :disabled="loading"
          @click="fetchHealth"
        >
          {{ loading ? 'Consultando...' : 'Actualizar' }}
        </button>
      </div>
      <div class="card-body">
        <div v-if="loading && !health" class="text-muted">Consultando estado...</div>

        <div v-else-if="errorMsg" class="alert alert-danger py-2 mb-3">
          {{ errorMsg }}
        </div>

        <template v-else-if="health">
          <div class="d-flex align-items-center mb-3">
            <span class="status-dot me-2" :class="statusClass"></span>
            <span class="fw-semibold">Backend: {{ statusLabel }}</span>
          </div>

          <div class="mb-3">
            <span class="badge" :class="latencyBadgeClass">
              Latencia: {{ latencyMs !== null ? `${latencyMs} ms` : '-' }}
            </span>
            <span v-if="latencyLabel" class="ms-2 small text-muted">
              {{ latencyLabel }}
            </span>
          </div>

          <ul class="list-unstyled mb-0 small">
            <li class="mb-2">
              <strong>Estado general:</strong> {{ health.status || '-' }}
            </li>
            <li class="mb-2">
              <strong>Base de datos:</strong> {{ health.services?.database || '-' }}
            </li>
            <li>
              <strong>Última actualización:</strong> {{ formatDateTime(health.timestamp) }}
            </li>
          </ul>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { healthApi } from '@/api/health';
import { normalizeApiError } from '@/utils/error-handler';

export default {
  name: 'BackendHealthCard',
  data() {
    return {
      loading: false,
      errorMsg: '',
      health: null,
      latencyMs: null,
      intervalId: null,
    };
  },
  computed: {
    isUp() {
      return this.health?.status === 'ok' && this.health?.services?.database === 'up';
    },
    statusLabel() {
      return this.isUp ? 'Operativo' : 'Con incidencias';
    },
    statusClass() {
      return this.isUp ? 'status-ok' : 'status-error';
    },
    latencyLabel() {
      if (this.latencyMs === null) return '';
      if (this.latencyMs < 200) return 'Respuesta rapida';
      if (this.latencyMs < 600) return 'Respuesta media';
      return 'Respuesta lenta';
    },
    latencyBadgeClass() {
      if (this.latencyMs === null) return 'badge-light';
      if (this.latencyMs < 200) return 'badge-success';
      if (this.latencyMs < 600) return 'badge-warning';
      return 'badge-danger';
    },
  },
  mounted() {
    this.fetchHealth();
    this.intervalId = setInterval(this.fetchHealth, 15000);
  },
  beforeUnmount() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  },
  methods: {
    async fetchHealth() {
      this.loading = true;
      this.errorMsg = '';
      const start = performance.now();
      try {
        const { data } = await healthApi.getStatus();
        this.health = data;
        this.latencyMs = Math.round(performance.now() - start);
      } catch (e) {
        this.errorMsg = normalizeApiError(e, 'No se pudo obtener el estado del backend.');
        this.latencyMs = null;
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(value) {
      if (!value) return '-';
      const date = new Date(value);
      return date.toLocaleString('es-ES');
    },
  },
};
</script>

<style scoped>
.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.status-ok {
  background-color: #198754;
}
.status-error {
  background-color: #dc3545;
}
</style>
